/**
 * One camera rig per phase, plus the impact juice.
 *
 * Two clocks, per types.ts: the rigs that *follow* something move on scaled
 * simulation time (so a chase cam lags correctly in slow-mo and freezes with
 * the ball during hitstop), while shake, sway and the result crane move on
 * unscaled time. Frozen shake during hitstop reads as a dropped frame.
 *
 * Everything here is framerate independent — exponential approaches and a
 * proper critically-damped smoothDamp, never a raw `lerp(a, b, 0.1)` — and
 * `update()` allocates nothing: every Vector3 is preallocated in the
 * constructor and three.js's own lookAt/rotate/translate paths use
 * module-scoped scratch.
 */

import { MathUtils, PerspectiveCamera, Vector3 } from 'three/webgpu'
import { BALL_REST_Y, MAX_FRAME, PIN, RAMP_TIME } from './tuning'
import { type Vec3, v3 } from './types'

export type CamPhase = 'AIM' | 'TEE' | 'FLIGHT' | 'PLANT' | 'GREEN' | 'CUP' | 'RESULT'

export type CamCtx = {
	ball: Vec3
	/** Where the flag is. The aim camera looks down the line at it. */
	pin?: { x: number; z: number }
	vel: Vec3
	/** Simulation seconds until impact; Infinity when no swing is inbound. */
	tToImpact: number
	timescale: number
}

// ── Camera constants ─────────────────────────────────────────────────────────
//
// These belong in tuning.ts later — they live here so this file never edits it.

export const CAM_NEAR = 0.02
export const CAM_FAR = 1400

/**
 * TEE — the dread cam. The tripod sits *downrange* of the ball and looks back:
 * the golfer swings in the YZ plane from -Z (see PIVOT in tuning.ts), so this is
 * the only side from which they can loom over the lens. Put the tripod at -Z
 * instead and their back fills the frame with the ball hidden behind it.
 *
 * Distance and height are set by what has to be legible at once: the ball's
 * height against the clubface (the whole game), and enough of the golfer that
 * their anger reads with the HUD off. At 1.1m the head sits 40 degrees up and
 * leaves the frame entirely, so the shot has to breathe more than "close".
 */
export const TEE_CAM_DIST = 2.2
export const TEE_CAM_Y = 0.55
export const TEE_CAM_PITCH = MathUtils.degToRad(11)
/**
 * The pitch is measured off the ball, not off the horizon: aim at a point this
 * far above the resting ball (roughly where the clubface arrives) and then tilt
 * up by TEE_CAM_PITCH. Anchoring to the ball is what keeps it in the bottom
 * third rather than under the bottom edge when the tee geometry in tuning.ts
 * moves — with a horizon-relative pitch, dropping TEE_TOP_Y by 4cm is enough to
 * push the ball out of frame at full punch.
 */
export const TEE_AIM_RISE = 0.16
/** Only the direction matters for framing; the distance sets the shake focus. */
export const TEE_LOOK_DIST = 2.2
/** Wide, because a wide lens is what makes a person loom. */
export const FOV_TEE = 52
/** The punch has to buy back the reach: at the moment of truth the contact zone
 *  should fill enough of the frame to read a centimetre of offset. */
export const FOV_TEE_PUNCH = 34

/**
 * AIM — the shot you get before the shot.
 *
 * The dread cam faces back at the golfer, which means that until now the player
 * never once saw where they were being aimed. On a course with bends, water and
 * trees that is not a style choice, it is missing information. So every stroke
 * now opens behind the ball looking down the line at the flag, holds long
 * enough to read the hole and the wind, and only then turns to face the man
 * with the club.
 */
export const AIM_BACK = 7
export const AIM_UP = 3.4
export const AIM_LOOK_AHEAD = 26
export const FOV_AIM = 50

/** Handheld nerves. Per-phase multiplier — CUP must be able to hold still. */
export const SWAY_DEG = 0.3
export const SWAY_FREQ = 0.55
export const SWAY_BY_PHASE: Record<CamPhase, number> = {
	AIM: 0.35,
	TEE: 1,
	FLIGHT: 0,
	PLANT: 0.6,
	GREEN: 0.5,
	CUP: 0,
	RESULT: 0.25,
}

/** FLIGHT — chase cam, FOV as a speedometer, roll as a slice indicator. */
export const FLIGHT_BACK = 5.5
export const FLIGHT_UP = 1.8
export const FLIGHT_SMOOTH = 0.25
export const FLIGHT_LOOK_SMOOTH = 0.12
export const FLIGHT_LOOK_LEAD = 0.35
/** Never let a steep climb drag the lens underground. */
export const FLIGHT_MIN_Y = 1.2
/** Below this speed the velocity direction is noise; keep the last good one. */
export const FLIGHT_DIR_MIN = 0.5
export const FOV_FLIGHT_BASE = 55
export const FOV_FLIGHT_GAIN = 18
export const FLIGHT_SPEED_REF = 90
export const BANK_MAX = MathUtils.degToRad(6)
export const BANK_ACCEL_REF = 6
export const BANK_SMOOTH = 0.14
/** Bounces are near-infinite dv/dt; clamp before filtering or the roll snaps. */
export const BANK_ACCEL_CLAMP = 12

/** PLANT — broadcast. Stop dead, pan, and pull to a long lens. */
export const PLANT_PAN_SMOOTH = 0.35
export const FOV_PLANT = 34

/** GREEN — low camera beside the cup, panning with the ball. */
export const GREEN_CAM_X = 5.0
export const GREEN_CAM_Y = 0.55
export const GREEN_CAM_Z = -2.5
export const GREEN_PAN_SMOOTH = 0.28
/** Bias the pan target toward the pin so the cup never leaves frame. */
export const GREEN_LOOK_BIAS = 0.3
export const FOV_GREEN = 40

/** CUP — straight up from the bottom of the hole. Deep enough that the rim
 *  rings the frame instead of falling outside it. */
export const CUP_CAM_Y = -0.34
export const CUP_LOOK_UP = 6
export const FOV_CUP = 62
/** Seconds of complete stillness in the hole before the camera moves at all. */
export const CUP_HOLD = 2.6
export const CUP_RISE_TIME = 4.5
export const CUP_RISE_Y = 7.5
export const FOV_CUP_END = 46

/**
 * RESULT — slow crane-up and back from the resting ball.
 *
 * The crane starts well clear of the turf on purpose: from 28cm up, a ball
 * lying in the rough fills the frame with a wall of grass and no horizon, so
 * the shot that is supposed to say "here is where it finished" says nothing.
 * The time matches how long the phase is actually held, so the move completes.
 */
export const RESULT_CRANE_TIME = 2.6
export const RESULT_BACK_0 = 4
export const RESULT_BACK_1 = 14
export const RESULT_Y_0 = 0.9
export const RESULT_Y_1 = 3
export const RESULT_SIDE_0 = 0.35
export const RESULT_SIDE_1 = 2.4
export const FOV_RESULT = 38

export const FOV_SMOOTH = 0.35
export const ROLL_SMOOTH = 0.18

/**
 * Trauma shake. Applied shake is trauma squared; trauma itself decays linearly.
 * Both amplitudes are *per axis* and three (resp. two) axes stack, so they are
 * trimmed below the nominal 1.2 deg / 0.8%: measured peak deviation at trauma 1
 * is 1.22 deg of rotation and 0.79% of viewport height.
 */
export const TRAUMA_DECAY = 1.2
export const SHAKE_ROT = MathUtils.degToRad(1.0)
/** Fraction of viewport height at the focus distance, per axis. */
export const SHAKE_TRANS = 0.007
export const SHAKE_FREQ = 21

export const REDUCED_MOTION_SCALE = 0.04

/** PLANT continues the flight shot, so its FOV eases. Every other change cuts. */
const CUT_TO: Record<CamPhase, boolean> = {
	AIM: true,
	TEE: true,
	FLIGHT: true,
	PLANT: false,
	GREEN: true,
	CUP: true,
	RESULT: true,
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Three incommensurate sines, weights summing to one so |noise1| <= 1. Cheap,
 * deterministic, seedable by phase offset — and no noise dependency, which for
 * two dozen calls a frame is the right trade.
 */
function noise1(t: number, seed: number): number {
	return (
		0.534 * Math.sin(t + seed * 1.71) +
		0.333 * Math.sin(t * 2.31 + seed * 4.13 + 1.3) +
		0.133 * Math.sin(t * 4.71 + seed * 9.77 + 2.6)
	)
}

/** Framerate-independent exponential approach; `tau` is the time constant. */
function approach(cur: number, target: number, tau: number, dt: number): number {
	return target + (cur - target) * Math.exp(-dt / Math.max(tau, 1e-4))
}

/**
 * Critically-damped spring (the classic Game Programming Gems smoothDamp).
 * `smoothTime` is roughly the time to reach the target; exact at any dt, and
 * it mutates `cur` and `vel` in place so it never allocates.
 */
function smoothDampVec3(
	cur: Vector3,
	target: Vector3,
	vel: Vector3,
	smoothTime: number,
	dt: number,
): void {
	const omega = 2 / Math.max(smoothTime, 1e-4)
	const x = omega * dt
	const decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)

	let change = cur.x - target.x
	let temp = (vel.x + omega * change) * dt
	vel.x = (vel.x - omega * temp) * decay
	cur.x = target.x + (change + temp) * decay

	change = cur.y - target.y
	temp = (vel.y + omega * change) * dt
	vel.y = (vel.y - omega * temp) * decay
	cur.y = target.y + (change + temp) * decay

	change = cur.z - target.z
	temp = (vel.z + omega * change) * dt
	vel.z = (vel.z - omega * temp) * decay
	cur.z = target.z + (change + temp) * decay
}

// ── The rig ──────────────────────────────────────────────────────────────────

export class Cameras {
	readonly camera: PerspectiveCamera

	private p: CamPhase = 'TEE'
	/** Unscaled clocks. `time` drives noise, `phaseTime` drives the crane. */
	private time = 0
	private phaseTime = 0

	private trauma = 0
	private motionScale = 1

	private fov = FOV_TEE
	private fovTarget = FOV_TEE
	private roll = 0
	private rollTarget = 0
	private latAccel = 0

	private readonly pos = new Vector3()
	private readonly look = new Vector3()
	/**
	 * The chase spring's state is the camera's offset *from the ball*, not its
	 * world position. A smoothDamp against a world-space target moving at 60m/s
	 * settles at a standing `smoothTime * speed` lag — 15m on top of the 5.5m
	 * offset, which parks the rig four times too far back and shrinks the ball
	 * to about three pixels. Springing in the ball's frame feeds its motion
	 * forward exactly, so the transient response is unchanged but the rig
	 * actually sits where FLIGHT_BACK says. Discontinuities that matter (the
	 * direction flip at a bounce) live in the offset target, so they are still
	 * damped rather than passed through.
	 */
	private readonly posOff = new Vector3()
	private readonly lookOff = new Vector3()
	private readonly posVel = new Vector3()
	private readonly lookVel = new Vector3()
	/** Where the ball was when the phase began: the TEE tripod's aim reference
	 *  and the RESULT crane's subject. Captured once, never followed. */
	private readonly anchor = new Vector3()
	private readonly plantPos = new Vector3()
	private readonly dir = new Vector3(0, 0, 1)
	private readonly right = new Vector3()
	private readonly prevVel = new Vector3()
	private readonly upVec = new Vector3(0, 1, 0)
	private readonly tmpA = new Vector3()
	private readonly tmpB = new Vector3()
	private readonly tmpC = new Vector3()

	constructor(aspect: number) {
		this.camera = new PerspectiveCamera(FOV_TEE, aspect, CAM_NEAR, CAM_FAR)

		const mq =
			typeof window !== 'undefined' && typeof window.matchMedia === 'function'
				? window.matchMedia('(prefers-reduced-motion: reduce)')
				: null
		if (mq) {
			this.motionScale = mq.matches ? REDUCED_MOTION_SCALE : 1
			mq.addEventListener('change', (e) => {
				this.motionScale = e.matches ? REDUCED_MOTION_SCALE : 1
			})
		}

		this.setPhase('TEE', {
			ball: v3(0, BALL_REST_Y, 0),
			vel: v3(),
			tToImpact: Number.POSITIVE_INFINITY,
			timescale: 1,
		})
	}

	get phase(): CamPhase {
		return this.p
	}

	resize(w: number, h: number): void {
		this.camera.aspect = w / Math.max(h, 1)
		this.camera.updateProjectionMatrix()
	}

	addTrauma(amount: number): void {
		this.trauma = Math.min(1, Math.max(0, this.trauma + amount))
	}

	setPhase(p: CamPhase, ctx: CamCtx): void {
		this.p = p
		this.phaseTime = 0
		// Looking straight up out of the cup is degenerate against a +Y up
		// vector, so the cup cam calls downrange "up" — which also puts the
		// flagstick sensibly in frame.
		this.upVec.set(0, p === 'CUP' ? 0 : 1, p === 'CUP' ? 1 : 0)

		switch (p) {
			case 'TEE':
				// Address height, captured once: the tripod does not follow the
				// squat or the hop, which is exactly what makes them readable.
				this.anchor.set(ctx.ball.x, ctx.ball.y, ctx.ball.z)
				break
			case 'FLIGHT': {
				// A cut, so place the chase rig at its ideal offset and kill the
				// spring velocity — otherwise it swoops in from the tee cam.
				this.tmpA.set(ctx.vel.x, ctx.vel.y, ctx.vel.z)
				const speed = this.tmpA.length()
				if (speed > FLIGHT_DIR_MIN) this.dir.copy(this.tmpA).multiplyScalar(1 / speed)
				this.flightOffsets(ctx, this.posOff, this.lookOff)
				this.posVel.set(0, 0, 0)
				this.lookVel.set(0, 0, 0)
				this.prevVel.copy(this.tmpA)
				this.latAccel = 0
				break
			}
			case 'PLANT':
				// The broadcast trick: freeze the dolly where it already is and
				// let only the pan continue. Keeping the current look target as
				// the pan's starting point is what makes the switch invisible.
				this.plantPos.copy(this.pos)
				this.lookVel.set(0, 0, 0)
				break
			case 'GREEN':
				this.pos.set(PIN.x + GREEN_CAM_X, GREEN_CAM_Y, PIN.z + GREEN_CAM_Z)
				this.look.set(ctx.ball.x, ctx.ball.y, ctx.ball.z)
				this.lookVel.set(0, 0, 0)
				break
			case 'CUP':
				this.pos.set(PIN.x, CUP_CAM_Y, PIN.z)
				this.look.set(PIN.x, CUP_CAM_Y + CUP_LOOK_UP, PIN.z)
				break
			case 'RESULT':
				this.anchor.set(ctx.ball.x, ctx.ball.y, ctx.ball.z)
				break
		}

		// dt = 0 leaves every smoother exactly where the branch above put it.
		this.rig(ctx, 0)
		if (CUT_TO[p]) {
			this.fov = this.fovTarget
			this.roll = this.rollTarget
		}
		this.camera.fov = this.fov
		this.camera.updateProjectionMatrix()
		this.applyTransform()
	}

	update(dtUnscaled: number, ctx: CamCtx): void {
		const dt = Math.min(Math.max(dtUnscaled, 0), MAX_FRAME)
		this.time += dt
		this.phaseTime += dt

		this.rig(ctx, dt * ctx.timescale)

		this.roll = approach(this.roll, this.rollTarget, ROLL_SMOOTH, dt)
		// The tee punch is an authored 0.35s curve; smoothing it would blunt it.
		this.fov =
			this.p === 'TEE' ? this.fovTarget : approach(this.fov, this.fovTarget, FOV_SMOOTH, dt)
		if (Math.abs(this.fov - this.camera.fov) > 1e-4) {
			this.camera.fov = this.fov
			this.camera.updateProjectionMatrix()
		}

		this.trauma = Math.max(0, this.trauma - TRAUMA_DECAY * dt)
		this.applyTransform()
	}

	// ── Per-phase rigs ───────────────────────────────────────────────────────

	private rig(ctx: CamCtx, dtSim: number): void {
		switch (this.p) {
			case 'TEE':
				this.rigTee(ctx)
				break
			case 'FLIGHT':
				this.rigFlight(ctx, dtSim)
				break
			case 'PLANT':
				this.rigPlant(ctx, dtSim)
				break
			case 'AIM':
				this.rigAim(ctx)
				break
			case 'GREEN':
				this.rigGreen(ctx, dtSim)
				break
			case 'CUP':
				this.rigCup()
				break
			case 'RESULT':
				this.rigResult()
				break
		}
	}

	/**
	 * Behind the ball, on the line to the flag, looking down it. Slides gently
	 * forward while it holds so the hole reads as a place rather than a diagram.
	 */
	private rigAim(ctx: CamCtx): void {
		const pin = ctx.pin ?? { x: ctx.ball.x, z: ctx.ball.z + 100 }
		const dx = pin.x - ctx.ball.x
		const dz = pin.z - ctx.ball.z
		const len = Math.hypot(dx, dz) || 1
		const ux = dx / len
		const uz = dz / len
		const creep = Math.min(1, this.phaseTime * 0.35)
		this.pos.set(
			ctx.ball.x - ux * (AIM_BACK - creep * 1.6),
			AIM_UP - creep * 0.5,
			ctx.ball.z - uz * (AIM_BACK - creep * 1.6),
		)
		// Look a fixed way down the line rather than at the flag itself, so a
		// four hundred metre par five does not stare at the horizon.
		const look = Math.min(len, AIM_LOOK_AHEAD + len * 0.35)
		this.look.set(ctx.ball.x + ux * look, 1.1, ctx.ball.z + uz * look)
		this.fovTarget = FOV_AIM
		this.rollTarget = 0
	}

	private rigTee(ctx: CamCtx): void {
		this.pos.set(this.anchor.x, TEE_CAM_Y, this.anchor.z + TEE_CAM_DIST)

		// Poor man's dolly zoom over the last RAMP_TIME seconds. Squared so the
		// squeeze arrives with the club rather than leading it — and it holds at
		// full punch through contact, because the caller owns the cut.
		const k = MathUtils.clamp(1 - ctx.tToImpact / RAMP_TIME, 0, 1)
		const punch = k * k
		this.fovTarget = FOV_TEE + (FOV_TEE_PUNCH - FOV_TEE) * punch

		// Two framings are needed and they fight: wide enough that the golfer's
		// head is in shot (anger has to read with the HUD off), then tight on
		// the contact zone at the moment of truth. So the dread tilt relaxes to
		// zero as the punch closes — otherwise zooming toward a point above the
		// ball crops the ball out exactly when it matters most.
		const el =
			Math.atan2(this.anchor.y + TEE_AIM_RISE * (1 - punch) - this.pos.y, TEE_CAM_DIST) +
			TEE_CAM_PITCH * (1 - punch)
		this.look.set(
			this.pos.x,
			this.pos.y + Math.sin(el) * TEE_LOOK_DIST,
			this.pos.z - Math.cos(el) * TEE_LOOK_DIST,
		)
		this.rollTarget = 0
	}

	/** Ideal camera and look-at offsets from the ball, in world axes. */
	private flightOffsets(ctx: CamCtx, outPos: Vector3, outLook: Vector3): void {
		outPos.copy(this.dir).multiplyScalar(-FLIGHT_BACK)
		// Clamped in ball-relative space so the spring cannot wind up against
		// the floor and then overshoot when the ball climbs away from it.
		outPos.y = Math.max(outPos.y + FLIGHT_UP, FLIGHT_MIN_Y - ctx.ball.y)
		// Lead the ball so the ground rush, not the ball, owns the frame.
		outLook.set(ctx.vel.x, ctx.vel.y, ctx.vel.z).multiplyScalar(FLIGHT_LOOK_LEAD)
	}

	private rigFlight(ctx: CamCtx, dt: number): void {
		this.tmpA.set(ctx.vel.x, ctx.vel.y, ctx.vel.z)
		const speed = this.tmpA.length()
		if (speed > FLIGHT_DIR_MIN) this.dir.copy(this.tmpA).multiplyScalar(1 / speed)

		this.flightOffsets(ctx, this.tmpB, this.tmpC)
		smoothDampVec3(this.posOff, this.tmpB, this.posVel, FLIGHT_SMOOTH, dt)
		smoothDampVec3(this.lookOff, this.tmpC, this.lookVel, FLIGHT_LOOK_SMOOTH, dt)
		this.pos.set(ctx.ball.x, ctx.ball.y, ctx.ball.z).add(this.posOff)
		this.look.set(ctx.ball.x, ctx.ball.y, ctx.ball.z).add(this.lookOff)

		this.fovTarget =
			FOV_FLIGHT_BASE + FOV_FLIGHT_GAIN * MathUtils.clamp(speed / FLIGHT_SPEED_REF, 0, 1)

		// Bank into lateral acceleration. This is what makes a slice, a scuffed
		// ball's flutter and a sidespin curve legible instead of merely true.
		if (dt > 1e-6) {
			this.right.set(0, 1, 0).cross(this.dir)
			const rl = this.right.length()
			if (rl > 1e-4) {
				this.right.multiplyScalar(1 / rl)
				const dv =
					(this.tmpA.x - this.prevVel.x) * this.right.x +
					(this.tmpA.y - this.prevVel.y) * this.right.y +
					(this.tmpA.z - this.prevVel.z) * this.right.z
				const raw = MathUtils.clamp(dv / dt, -BANK_ACCEL_CLAMP, BANK_ACCEL_CLAMP)
				this.latAccel = approach(this.latAccel, raw, BANK_SMOOTH, dt)
			}
			this.prevVel.copy(this.tmpA)
		}
		// Negative: curving right rolls the lens right, so the horizon tips left.
		this.rollTarget = -MathUtils.clamp(this.latAccel / BANK_ACCEL_REF, -1, 1) * BANK_MAX
	}

	private rigPlant(ctx: CamCtx, dt: number): void {
		this.pos.copy(this.plantPos)
		this.tmpB.set(ctx.ball.x, ctx.ball.y, ctx.ball.z)
		smoothDampVec3(this.look, this.tmpB, this.lookVel, PLANT_PAN_SMOOTH, dt)
		this.fovTarget = FOV_PLANT
		this.rollTarget = 0
	}

	private rigGreen(ctx: CamCtx, dt: number): void {
		this.pos.set(PIN.x + GREEN_CAM_X, GREEN_CAM_Y, PIN.z + GREEN_CAM_Z)
		this.tmpB.set(ctx.ball.x, ctx.ball.y, ctx.ball.z)
		this.tmpA.set(PIN.x, 0, PIN.z)
		this.tmpB.lerp(this.tmpA, GREEN_LOOK_BIAS)
		smoothDampVec3(this.look, this.tmpB, this.lookVel, GREEN_PAN_SMOOTH, dt)
		this.fovTarget = FOV_GREEN
		this.rollTarget = 0
	}

	/**
	 * From the bottom of the cup, looking up at a circle of sky — then, once the
	 * silence has had its time, rising slowly out of the hole. The move starts
	 * late and takes its time on purpose: the beat only works if the stillness
	 * lasts long enough to become uncomfortable.
	 */
	private rigCup(): void {
		const t = MathUtils.clamp((this.phaseTime - CUP_HOLD) / CUP_RISE_TIME, 0, 1)
		const e = t * t * (3 - 2 * t)
		this.pos.set(PIN.x, MathUtils.lerp(CUP_CAM_Y, CUP_RISE_Y, e), PIN.z)
		this.look.set(PIN.x, this.pos.y + CUP_LOOK_UP, PIN.z)
		this.fovTarget = MathUtils.lerp(FOV_CUP, FOV_CUP_END, e)
		this.rollTarget = 0
	}

	private rigResult(): void {
		const t = MathUtils.clamp(this.phaseTime / RESULT_CRANE_TIME, 0, 1)
		const e = 1 - (1 - t) ** 3
		this.pos.set(
			this.anchor.x + MathUtils.lerp(RESULT_SIDE_0, RESULT_SIDE_1, e),
			MathUtils.lerp(RESULT_Y_0, RESULT_Y_1, e),
			this.anchor.z - MathUtils.lerp(RESULT_BACK_0, RESULT_BACK_1, e),
		)
		this.look.copy(this.anchor)
		this.fovTarget = FOV_RESULT
		this.rollTarget = 0
	}

	// ── Presentation ─────────────────────────────────────────────────────────

	/** Base transform, then bank, sway and trauma stacked in local space. */
	private applyTransform(): void {
		const cam = this.camera
		cam.up.copy(this.upVec)
		cam.position.copy(this.pos)
		cam.lookAt(this.look)

		if (Math.abs(this.roll) > 1e-5) cam.rotateZ(this.roll)

		// Nerves: low-frequency, sub-degree, never enough to notice on purpose.
		const sway = SWAY_BY_PHASE[this.p] * this.motionScale
		if (sway > 1e-4) {
			const st = this.time * SWAY_FREQ
			const amp = MathUtils.degToRad(SWAY_DEG) * sway
			cam.rotateX(amp * noise1(st, 11))
			cam.rotateY(amp * noise1(st, 12))
		}

		// Trauma squared: a light tap barely registers, a flush strike hammers.
		const s = this.trauma * this.trauma * this.motionScale
		if (s > 1e-4) {
			const t = this.time * SHAKE_FREQ
			cam.rotateX(SHAKE_ROT * s * noise1(t, 1))
			cam.rotateY(SHAKE_ROT * s * noise1(t, 2))
			cam.rotateZ(SHAKE_ROT * s * noise1(t, 3))

			// Translate in fractions of the viewport at the focus distance, so
			// the shake reads the same at 34mm on the green and 73mm in flight.
			const focus = Math.max(this.pos.distanceTo(this.look), 0.5)
			const viewH = 2 * Math.tan(MathUtils.degToRad(this.fov) * 0.5) * focus
			cam.translateX(SHAKE_TRANS * s * viewH * noise1(t, 4))
			cam.translateY(SHAKE_TRANS * s * viewH * noise1(t, 5))
		}
	}
}
